import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';
import assert from 'assert';

import typeCheck from 'common/util/typeCheck';
import {
  USER_ROLE_ANONYMOUS,
  USER_ROLE_AGENT,
  USER_ROLE_APARTMENT_LOCATOR,
  USER_ROLE_ADMIN,
  hasRoleAdmin,
  hasRolePartner,
  hasRoleLandlord,
  hasRoleDriver,
} from 'common/userRoles';
import { getTourToReschedule } from 'common/tourHelpers';

import branch from 'client/core/branch';
import log from 'client/lib/log';
import configureDecoratedComponent from 'client/helpers/configureDecoratedComponent';

const debug = log.debug('sunroom:decorators:allowedRoles');

export function getUserLandingPage({ userRoles, tours, isMobileApp }) {

  const afterSignupLink = '/feed';

  if (hasRolePartner(userRoles) && !hasRoleAdmin(userRoles)) {
    return '/partner';
  }
  if (hasRoleDriver(userRoles)) {
    return '/sunsetup/driver';
  }
  if (hasRoleLandlord(userRoles)) {
    return '/owner';
  }
  if (userRoles.includes(USER_ROLE_ANONYMOUS)) {
    return '/';
  }
  if (userRoles.includes(USER_ROLE_AGENT)) {
    return '/agent/tours';
  }
  if (userRoles.includes(USER_ROLE_APARTMENT_LOCATOR)) {
    return '/agent/locator-profile';
  }
  if (userRoles.includes(USER_ROLE_ADMIN) && !isMobileApp) {
    return '/admin';
  }
  if (tours && getTourToReschedule({ userTours: tours })) {
    return '/tour';
  }
  return afterSignupLink;
}

export default function allowedRoles(params) {
  typeCheck('params::NonEmptyObject', params);

  const {
    roles,
    redirectUrl,
    redirectToUserLandingPage,
    addRedirectQueryParam,
  } = params;

  typeCheck('roles::NonEmptyArray', roles);

  assert(
    !!redirectUrl ^ !!redirectToUserLandingPage,
    'Either redirectUrl or redirectToUserLandingPage must be set, but not both.'
  );

  assert(
    !addRedirectQueryParam || !redirectToUserLandingPage,
    'Can not use addRedirectQueryParam and redirectToUserLandingPage simultaneously.'
  );

  return function allowedRolesDecorator(ComponentToDecorate) {
    @branch({
      currentUserRoles: ['currentUser', 'roles'],
      tours: ['tours'],
    })
    @withRouter
    class DecoratedComponent extends Component {
      static propTypes = {
        currentUserRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
        tours: PropTypes.array,
        location: PropTypes.object.isRequired,
      }

      render() {
        const { currentUserRoles, tours, location } = this.props;
        const locationQuery = queryString.parse(location.search);
        const isAllowed = roles.some((allowedRole) => currentUserRoles.includes(allowedRole));

        const redirectTo = (
          locationQuery.next
          || redirectUrl
          || getUserLandingPage({ userRoles: currentUserRoles, tours: tours })
        );

        if (!isAllowed) {
          debug(
            `Current user roles "${currentUserRoles.join(', ')}" not allowed in ` +
            `"${ComponentToDecorate.displayName}", redirecting to ${redirectTo}`
          );
        }

        return isAllowed
          ? <ComponentToDecorate {...this.props} />
          : (
            <Redirect
              to={
                `${redirectTo}${addRedirectQueryParam
                  ? '?next=' + encodeURIComponent(location.pathname)
                  : ''
                }`
              }
            />
          );
      }
    }

    configureDecoratedComponent({
      DecoratedComponent: DecoratedComponent,
      OriginalComponent: ComponentToDecorate,
      displayNameSuffix: 'AllowedRoles',
    });

    return DecoratedComponent;
  };
}
